import React, { useEffect } from 'react';
import {
	setActiveHotspotId,
	setHotspots,
	setNavMarkers,
} from '@wsc/redux_stores/accessibilityReducer';
import srSpeak from '@wsc/utils/adaHelpers';
import { createSelector } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';

const accSelector = createSelector(
	[(state) => state?.accessibility],
	(accessibility) => ({
		accessibilitySelector: accessibility.accessibilitySelector,
		accessibilityControlsEnabled:
			accessibility.accessibilityControlsEnabled,
		navMarkers: accessibility.navMarkers,
		hotspots: accessibility.hotspots,
	}),
);

const roomObjectsSelector = createSelector(
	(state) => state.roomObjects,
	(roomObjects) => {
		return Object.keys(roomObjects).map((key) => {
			const roomObject = roomObjects[key];
			return {
				_id: roomObject._id.$oid,
				type: roomObject.type,
				hotspotType: roomObject?.props?.hotspot_type,
				isIconGlb:
					roomObject?.props?.icon?.path?.includes('.glb') || false,
			};
		});
	},
);

const disableForHotspotTypes = [
	'navigation_label',
	'3d_model',
	'animation',
	'tooltip',
	'embedded_video',
	'embedded_image',
];

const AccessibilityListener = () => {
	/**
	 * Accessibility listener for three scene
	 *
	 * This component is responsible for listening to keyboard events and dispatching appropriate actions to the accessibility reducer
	 *
	 * `alt/options` key is used as the modifier key for accessibility controls
	 * `ArrowLeft` and `ArrowRight` keys are used for navigation controls
	 * `ArrowUp` and `ArrowDown` keys are used for hotspot controls
	 *
	 * **/
	const { accessibilityControlsEnabled, navMarkers, hotspots } =
		useSelector(accSelector);

	const roomObjects = useSelector(roomObjectsSelector);

	const dispatch = useDispatch();
	const navigationRefIndex = React.useRef(null);
	const hotspotRefIndex = React.useRef(null);
	const navMarkerCount = navMarkers.length;
	const hotspotMarkerCount = hotspots.length;

	const setActiveRef = (ix, selector) => {
		if (selector === null || ix === null) {
			navigationRefIndex.current = null;
			hotspotRefIndex.current = null;
			return;
		}

		const selectedHotspot =
			selector === 'navigation' ? navMarkers[ix] : hotspots[ix];

		dispatch(setActiveHotspotId(selectedHotspot._id));

		if (selector === 'hotspot') hotspotRefIndex.current = ix;
		if (selector === 'navigation') navigationRefIndex.current = ix;

		const speakPrompt =
			selector === 'navigation'
				? 'Navigation arrow'
				: `${selectedHotspot?.hotspotType?.replaceAll(
						'_',
						' ',
				  )} hotspot`;

		srSpeak(speakPrompt);
	};

	const keyUpListener = (e) => {
		if (!accessibilityControlsEnabled) return;
		e.preventDefault();
		e.stopPropagation();

		// If alt key is not pressed, reset refIndex
		if (e.altKey === false) {
			setActiveRef(null, null);
			return;
		}

		// Switches accesibility modes between navigation and hotspot
		const newSelector =
			e.key === 'ArrowLeft' || e.key === 'ArrowRight'
				? 'navigation'
				: e.key === 'ArrowUp' || e.key === 'ArrowDown'
				? 'hotspot'
				: null;

		if (newSelector === null) return;

		const selectedIndex =
			newSelector === 'navigation'
				? navigationRefIndex.current
				: hotspotRefIndex.current;

		if (selectedIndex === null) {
			document.activeElement.blur();
			setActiveRef(0, newSelector);
			return;
		}

		let updatedRefIndex = selectedIndex;

		switch (e.key) {
			case 'ArrowLeft':
				updatedRefIndex -= 1;
				break;
			case 'ArrowRight':
				updatedRefIndex += 1;
				break;
			case 'ArrowUp':
				updatedRefIndex += 1;
				break;
			case 'ArrowDown':
				updatedRefIndex -= 1;
				break;
			default:
				break;
		}

		const countToCompare =
			newSelector === 'navigation' ? navMarkerCount : hotspotMarkerCount;

		if (updatedRefIndex < 0) {
			updatedRefIndex = countToCompare - 1;
		} else if (updatedRefIndex >= countToCompare) {
			updatedRefIndex = 0;
		}

		setActiveRef(updatedRefIndex, newSelector);
		navigationRefIndex.current = updatedRefIndex;
	};

	const countMarkers = () => {
		if (!roomObjects) return;
		const _navMarkers = [];
		const _hotspotMarkers = [];
		roomObjects.forEach((item) => {
			if (item.type === 'NavMarker') {
				_navMarkers.push(item);
			} else if (
				item.type !== 'NavMarker' &&
				!disableForHotspotTypes.includes(item.hotspotType) &&
				item.isIconGlb === false
			) {
				_hotspotMarkers.push(item);
			}
		});
		dispatch(setNavMarkers(_navMarkers));
		dispatch(setHotspots(_hotspotMarkers));
	};

	useEffect(() => {
		countMarkers();
	}, [roomObjects]);

	useEffect(() => {
		document.addEventListener('keyup', keyUpListener);

		return () => {
			document.removeEventListener('keyup', keyUpListener);
		};
	}, [navMarkers, hotspots]);

	return <></>;
};

export default AccessibilityListener;
